import {  useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import { initiateUserAuth } from 'common/auth/modalLogin';
import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';
import { paymentInitialize } from 'common/razorpay/paymentInitialize';
import MoneyDisplay from 'common/components/MoneyDisplay';
import CopyTextButton from 'v3/common/components/CopyTextButton';

const propTypes = {
  staticIpPlan: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    strike_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    duration_in_days: PropTypes.number.isRequired,
  }).isRequired,
  credits: PropTypes.number,
  userStaticIp: PropTypes.shape({
    has_static_ip: PropTypes.bool,
    ip_address: PropTypes.string,
    active_till: PropTypes.string,
    assigned_at: PropTypes.string,
    is_expired: PropTypes.bool,
    is_in_grace_period: PropTypes.bool,
    grace_period_end_date: PropTypes.string,
    plan_name: PropTypes.string,
  }),
  isIpMandatedBroker: PropTypes.bool,
};

const defaultProps = {
  credits: 0,
  userStaticIp: null,
};

const StaticIpCard = ({ staticIpPlan, credits, userStaticIp, isIpMandatedBroker }) => {
  const { id, price, strike_price: strikePrice, duration_in_days: durationInDays } = staticIpPlan;

  // eslint-disable-next-line no-undef
  const [isUserSignedIn, setIsUserSignedIn] = useState(window.isUserSignedIn);
  const [multiplier, setMultiplier] = useState(1);
  const [showRenewCard, setShowRenewCard] = useState(false);

  // eslint-disable-next-line no-undef
  const { hasPaymentEnabled } = window;
  // eslint-disable-next-line no-undef
  const isStaticIpPurchaseDisabled = window.SA_FEATURE_FLAGS?.disableStaticIpPurchaseBtn || false;
  const isPositiveCreditBalance = _.isNumber(credits) && credits > 0;

  // Extract user static IP info
  const hasStaticIp = userStaticIp?.has_static_ip;
  const isExpired = userStaticIp?.is_expired;
  const isInGracePeriod = userStaticIp?.is_in_grace_period;
  const ipAddress = userStaticIp?.ip_address;
  const activeTill = userStaticIp?.active_till;
  const gracePeriodEndDate = userStaticIp?.grace_period_end_date;

  // Determine the current state
  const isActiveAndValid = hasStaticIp && !isExpired;
  const isExpiredInGracePeriod = hasStaticIp && isExpired && isInGracePeriod;
  // Clean state: no static IP OR expired and past grace period (proxy will be unassigned by cron)

  // Calculate remaining days
  const getRemainingDays = () => {
    if (!activeTill) return 0;
    const today = moment().startOf('day');
    const expiry = moment(activeTill).startOf('day');
    const diff = expiry.diff(today, 'days');
    return Math.max(0, diff);
  };

  // Calculate grace period remaining days
  const getGracePeriodRemainingDays = () => {
    if (!gracePeriodEndDate) return 0;
    const today = moment().startOf('day');
    const graceEnd = moment(gracePeriodEndDate).startOf('day');
    const diff = graceEnd.diff(today, 'days');
    return Math.max(0, diff);
  };

  const remainingDays = getRemainingDays();
  const gracePeriodRemainingDays = getGracePeriodRemainingDays();

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return moment(dateStr).format('MMMM D, YYYY');
  };

  const redirectToStaticIpTab = () => {
    // Redirect to plans page with static-ip tab selected
    // eslint-disable-next-line no-undef
    window.location.href = '/plans?tab=static-ip';
  };

  const onCreatePayment = () => {
    const totalDays = durationInDays * multiplier;

    const args = {
      static_ip_plan_id: id,
      purchase_type: 'static_ip',
      credits_applied: isPositiveCreditBalance,
      multiplier,
      total_days: totalDays,
    };

    paymentInitialize(args, () => {
      setTimeout(() => {
        redirectToStaticIpTab();
      }, 1.5 * 1000);
    });
  };

  const onSubmit = () => {
    if (!isUserSignedIn) {
      return initiateUserAuth({ action: 'static_ip_purchase' })
        .then(() => {
          setIsUserSignedIn(true);
          redirectToStaticIpTab();
        })
        .catch(() => {
          reactToastify('Unauthorized, Please login and Continue', TOASTIFY_TYPES.ERROR);
        });
    }

    return onCreatePayment();
  };

  const incrementMultiplier = () => {
    if (multiplier < 2) setMultiplier(multiplier + 1);
  };

  const decrementMultiplier = () => {
    if (multiplier > 1) setMultiplier(multiplier - 1);
  };

  const totalPrice = Number(price) * multiplier;
  const totalStrikePrice = strikePrice ? Number(strikePrice) * multiplier : null;
  const totalDays = durationInDays * multiplier;

  const renderQuantitySelector = () => (
    <div className="quantity-selector">
      <span className="text-muted tx-12">Plan Duration</span>
      <div className="quantity-controls">
        <button
          type="button"
          onClick={decrementMultiplier}
          disabled={multiplier <= 1}
          className="btn btn-sm border-0 "
        >
          -
        </button>
        <h6 className="px-2">{multiplier}</h6>
        <button
          type="button"
          onClick={incrementMultiplier}
          disabled={multiplier >= 2}
          className="btn btn-sm border-0 "
        >
          +
        </button>
      </div>
    </div>
  );

  const renderPriceDisplay = (withBackground = false) => (
    <div
      className="amount-display"
      style={
        withBackground
          ? {
              backgroundColor: 'var(--card-wrapper-bg)',
            }
          : {}
      }
    >
      {totalStrikePrice && (
        <del className="text-muted tx-14">
          <MoneyDisplay>{totalStrikePrice}</MoneyDisplay>
        </del>
      )}
      <span className="h5 mb-0 fw-bold">
        <MoneyDisplay>{totalPrice}</MoneyDisplay>
      </span>
      <span className="gst-label">+ GST</span>
    </div>
  );

  // State 4: Expired but in Grace Period - Show warning with IP and urgent renew
  if (isExpiredInGracePeriod && !showRenewCard) {
    return (
      <div className="static-ip-plan-box ">
        <div className="static-ip-header">
          <h6 className="d-flex ">
            <i className="fa-regular fa-globe" />
            Dedicated Static IP
          </h6>
          <div className="badge">
            <span className="expired-badge">EXPIRED</span>
          </div>
        </div>

        <div className="text-danger">
          <i className="fa-regular fa-triangle-exclamation me-2"></i>
          Your Static IP has expired! Renew within {gracePeriodRemainingDays}{' '}
          {gracePeriodRemainingDays === 1 ? 'day' : 'days'} to keep the same IP.
        </div>

        <div className="ip-address-copy-card">
          <p>Your IP Address</p>
          <div className="d-flex align-items-center justify-content-between">
            <span className="h5 mb-0" style={{ fontWeight: '500' }}>
              {ipAddress}
            </span>
            <CopyTextButton text={ipAddress} className="btn copy-btn p-1" />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowRenewCard(true)}
          disabled={!hasPaymentEnabled || isStaticIpPurchaseDisabled}
          className="btn static-ip-btn  w-100"
        >
          Renew Now
        </button>
      </div>
    );
  }

  // State 4b: Expired in Grace Period - Renew Card
  if (isExpiredInGracePeriod && showRenewCard) {
    return (
      <div className="static-ip-plan-box">
        <div className="static-ip-header flex-column align-items-start gap-2">
          <h6 className="d-flex gap-1">Renew Dedicated Static IP</h6>
          <p className="text-danger">
            <i className="fa-regular fa-triangle-exclamation me-2"></i>
            Expired! Grace period ends {formatExpiryDate(gracePeriodEndDate)}
          </p>
        </div>

        <div className="subscription-period-box" style={{ border: '1px solid #e9ecef' }}>
          <p>New Subscription Period</p>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="day-active-badge text-white">{totalDays} Days</span>
          </div>
          <p className="text-muted tx-11 mb-0">Starting from today</p>
        </div>

        {renderPriceDisplay(true)}
        {renderQuantitySelector()}

        <button
          type="button"
          onClick={onSubmit}
          disabled={!hasPaymentEnabled || isStaticIpPurchaseDisabled}
          className="btn static-ip-btn  w-100 mb-2"
        >
          Renew Now
        </button>

        <button
          type="button"
          onClick={() => setShowRenewCard(false)}
          className="btn static-ip-btn__outline w-100"
        >
          Back
        </button>
      </div>
    );
  }

  // State 3: Active Static IP - Show IP with ACTIVE badge
  if (isActiveAndValid && !showRenewCard) {
    return (
      <div className="static-ip-plan-box">
        <div className="static-ip-header ">
          <h6 className="d-flex gap-1">
            <i className="fa-regular fa-globe" />
            Dedicated Static IP
          </h6>
          <div className="badge">
            <span className="active-badge">ACTIVE</span>
          </div>
        </div>
        <p>Expires on {formatExpiryDate(activeTill)}</p>

        <div className="ip-address-copy-card">
          <p className="text-muted tx-11 mb-2 text-start">Your IP Address</p>
          <div className="d-flex align-items-center justify-content-between">
            <span className="h5 mb-0" style={{ fontWeight: '500' }}>
              {ipAddress}
            </span>
            <CopyTextButton text={ipAddress} className="copy-btn" />
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowRenewCard(true)}
          disabled={!hasPaymentEnabled || isStaticIpPurchaseDisabled}
          className="btn static-ip-btn w-100"
        >
          Renew In Advance
        </button>
      </div>
    );
  }

  // State 2: Renew Card - Show renewal options with remaining + new days
  if (isActiveAndValid && showRenewCard) {
    return (
      <div className="static-ip-plan-box">
        <h6 className="mb-1 text-start fw-bold">Renew Static IP</h6>
        <p>
          Current Expires on <span className="expiry-date-bold">{formatExpiryDate(activeTill)}</span>
        </p>

        <div className="subscription-period-box">
          <p>Subscription Period</p>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="day-badge">{remainingDays} Days</span>
            <span className="text-muted">+</span>
            <span className="day-active-badge text-white">{totalDays} Days</span>
          </div>
          <p className="text-muted tx-11 mb-0">Current Active + Renewal period</p>
        </div>

        {renderPriceDisplay(true)}
        {renderQuantitySelector()}

        <button
          type="button"
          onClick={onSubmit}
          disabled={!hasPaymentEnabled || isStaticIpPurchaseDisabled}
          className="btn static-ip-btn w-100"
        >
          Renew In Advance
        </button>

        <button
          type="button"
          onClick={() => setShowRenewCard(false)}
          className="btn static-ip-btn__outline w-100"
        >
          View Dedicated Static IP
        </button>
      </div>
    );
  }

  const shouldDisableBtn = !hasPaymentEnabled || !isIpMandatedBroker || isStaticIpPurchaseDisabled;

  // State 1: Clean State - New purchase card (no IP or grace period ended)
  return (
    <div className="static-ip-plan-box">
      <div className="static-ip-header ">
        <h6 className="d-flex ">
          <i className="fa-regular fa-globe" />
          Dedicated Static IP
        </h6>
      </div>
      <div className="content">
        <p>{durationInDays * multiplier} DAYS</p>
        {renderPriceDisplay()}
      </div>
      {renderQuantitySelector()}

      <button
        type="button"
        onClick={onSubmit}
        disabled={shouldDisableBtn}
        className={`btn static-ip-btn w-100`}
      >
        Get Dedicated Static IP
      </button>
    </div>
  );
};

StaticIpCard.propTypes = propTypes;
StaticIpCard.defaultProps = defaultProps;

export default StaticIpCard;
